import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { Spinner } from '@/components/Spinner';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { apiGetPaginated } from '@/features/_shared/queries';
import { TenantContext } from '@/lib/tenant';
import type { MeOut } from '@/lib/auth';

/** Two-letter avatar initials from the signed-in user's name, else e-mail. */
function initialsFor(user: MeOut): string {
  const name = user.full_name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    const initials = (first + last).toUpperCase();
    if (initials) return initials;
  }
  return user.email.slice(0, 2).toUpperCase();
}

/**
 * Maps a matched route path to a NL document title.
 * Falls back to the SerSo brand when no specific title is configured.
 */
const TITLE_BY_PATH: Array<{ test: RegExp; title: string }> = [
  { test: /^\/login$/, title: 'Inloggen' },
  { test: /^\/$/, title: 'Dashboard' },
  { test: /^\/tenants$/, title: 'Tenants' },
  { test: /^\/tenants\/[^/]+$/, title: 'Tenant' },
  { test: /^\/tenants\/[^/]+\/domains$/, title: 'Domeinen' },
  { test: /^\/tenants\/[^/]+\/domains\/[^/]+$/, title: 'Domein' },
  { test: /^\/tenants\/[^/]+\/reports$/, title: 'Rapporten' },
  { test: /^\/tenants\/[^/]+\/reports\/[^/]+$/, title: 'Rapport' },
  { test: /^\/tenants\/[^/]+\/sources$/, title: 'Bronnen' },
  { test: /^\/tenants\/[^/]+\/recipients$/, title: 'Ontvangers' },
  { test: /^\/tenants\/[^/]+\/deliveries$/, title: 'Verzendingen' },
  { test: /^\/tenants\/[^/]+\/alerts$/, title: 'Alerts' },
  { test: /^\/tenants\/[^/]+\/settings$/, title: 'Instellingen' },
];

function resolveTitle(pathname: string): string {
  const match = TITLE_BY_PATH.find((entry) => entry.test.test(pathname));
  return match ? `RuaHQ · ${match.title}` : 'RuaHQ';
}

type TenantSummary = { slug: string; name: string };

const SWITCHER_PER_PAGE = 200;

/**
 * Fetches every tenant page so the switcher lists all tenants, not just the
 * first page. `/tenants` is paginated (default per_page=50); we request a large
 * per_page and loop any remaining pages, concatenating into one array.
 */
async function fetchAllTenants(): Promise<TenantSummary[]> {
  const first = await apiGetPaginated<TenantSummary>(
    `/tenants?page=1&per_page=${SWITCHER_PER_PAGE}`,
    { tenant: 'all' },
  );
  const all = [...first.data];
  const pages = first.meta?.pages ?? 1;
  for (let page = 2; page <= pages; page += 1) {
    const next = await apiGetPaginated<TenantSummary>(
      `/tenants?page=${page}&per_page=${SWITCHER_PER_PAGE}`,
      { tenant: 'all' },
    );
    all.push(...next.data);
  }
  return all;
}

/**
 * Authenticated app shell. Fetches the full tenant list and feeds the sidebar's
 * TenantSwitcher, which previously always received an empty list. Uses a
 * switcher-specific query key (the shared ['tenants'] key holds a single-page,
 * differently-shaped payload for GlobalDashboard/TenantList).
 */
function AuthedShell({ user }: { user: MeOut }) {
  const tenantsQ = useQuery({
    queryKey: ['tenants', 'switcher', 'all'],
    queryFn: fetchAllTenants,
  });
  const tenants = (tenantsQ.data ?? []).map((t) => ({ slug: t.slug, name: t.name }));
  return (
    <Layout
      tenants={tenants}
      userInitials={initialsFor(user)}
      userLabel={user.full_name?.trim() || user.email}
    >
      <Outlet />
    </Layout>
  );
}

function RootShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [slug, setSlug] = useState<string | 'all'>('all');

  useEffect(() => {
    document.title = resolveTitle(pathname);
  }, [pathname]);

  // The /login route is rendered without the chrome (sidebar + topbar).
  const isLogin = pathname.startsWith('/login');

  if (isLogin) {
    return (
      <TenantContext.Provider value={{ slug, setSlug }}>
        <Outlet />
      </TenantContext.Provider>
    );
  }

  // Every non-login route is gated: RequireAuth resolves the session (trying a
  // cookie refresh on boot) and redirects to /login when there is none. Without
  // this wrapper the app chrome + routes render for anonymous users (the API
  // still 401s, but the UI should never show).
  return (
    <TenantContext.Provider value={{ slug, setSlug }}>
      <RequireAuth fallback={<div className="grid min-h-screen place-items-center"><Spinner /></div>}>
        {(user) => <AuthedShell user={user} />}
      </RequireAuth>
    </TenantContext.Provider>
  );
}

export const Route = createRootRoute({
  component: RootShell,
});
